// Synthetic Web Audio API sound system. Sounds evolve with the era.
let ctx = null;

const getCtx = () => {
  if (typeof window === "undefined") return null;
  if (!ctx) {
    try {
      const AC = window.AudioContext || window.webkitAudioContext;
      ctx = new AC();
    } catch {
      return null;
    }
  }
  if (ctx.state === "suspended") ctx.resume().catch(() => {});
  return ctx;
};

const envelope = (gain, start, peak, sustain, release, now) => {
  gain.gain.cancelScheduledValues(now);
  gain.gain.setValueAtTime(0, now);
  gain.gain.linearRampToValueAtTime(peak, now + start);
  gain.gain.exponentialRampToValueAtTime(
    Math.max(0.0001, sustain),
    now + start + 0.02,
  );
  gain.gain.exponentialRampToValueAtTime(0.0001, now + start + release);
};

// Play a single oscillator note with envelope.
const playNote = ({
  freq,
  type = "sine",
  duration = 0.15,
  volume = 0.15,
  detune = 0,
  filterFreq = null,
}) => {
  const ac = getCtx();
  if (!ac) return;
  const now = ac.currentTime;
  const osc = ac.createOscillator();
  const gain = ac.createGain();
  osc.type = type;
  osc.frequency.setValueAtTime(freq, now);
  osc.detune.setValueAtTime(detune, now);
  let node = osc;
  if (filterFreq) {
    const filter = ac.createBiquadFilter();
    filter.type = "lowpass";
    filter.frequency.setValueAtTime(filterFreq, now);
    osc.connect(filter);
    node = filter;
  }
  node.connect(gain);
  gain.connect(ac.destination);
  envelope(gain, 0.005, volume, volume * 0.5, duration, now);
  osc.start(now);
  osc.stop(now + duration + 0.05);
};

// Short noise burst (used for wood/knock sounds).
const playNoise = ({ duration = 0.08, volume = 0.15, filterFreq = 800 }) => {
  const ac = getCtx();
  if (!ac) return;
  const now = ac.currentTime;
  const bufferSize = Math.floor(ac.sampleRate * duration);
  const buffer = ac.createBuffer(1, bufferSize, ac.sampleRate);
  const data = buffer.getChannelData(0);
  for (let i = 0; i < bufferSize; i += 1) {
    data[i] = (Math.random() * 2 - 1) * (1 - i / bufferSize);
  }
  const src = ac.createBufferSource();
  src.buffer = buffer;
  const filter = ac.createBiquadFilter();
  filter.type = "lowpass";
  filter.frequency.setValueAtTime(filterFreq, now);
  const gain = ac.createGain();
  gain.gain.setValueAtTime(volume, now);
  gain.gain.exponentialRampToValueAtTime(0.0001, now + duration);
  src.connect(filter);
  filter.connect(gain);
  gain.connect(ac.destination);
  src.start(now);
  src.stop(now + duration);
};

// Map era -> click sound profile.
// All eras now share a skateboard trick sound (pop + flip whoosh).
const playSkateTrick = () => {
  const ac = getCtx();
  if (!ac) return;
  const now = ac.currentTime;

  // 1. Pop — sharp filtered noise burst representing the tail hitting the ground.
  const popDur = 0.09;
  const popBuf = ac.createBuffer(1, Math.floor(ac.sampleRate * popDur), ac.sampleRate);
  const popData = popBuf.getChannelData(0);
  for (let i = 0; i < popData.length; i += 1) {
    // Attack-heavy noise: fast decay
    const t = i / popData.length;
    popData[i] = (Math.random() * 2 - 1) * Math.pow(1 - t, 2.5);
  }
  const popSrc = ac.createBufferSource();
  popSrc.buffer = popBuf;
  const popFilter = ac.createBiquadFilter();
  popFilter.type = "bandpass";
  popFilter.frequency.setValueAtTime(1400, now);
  popFilter.Q.setValueAtTime(2.5, now);
  const popGain = ac.createGain();
  popGain.gain.setValueAtTime(0.35, now);
  popGain.gain.exponentialRampToValueAtTime(0.0001, now + popDur);
  popSrc.connect(popFilter);
  popFilter.connect(popGain);
  popGain.connect(ac.destination);
  popSrc.start(now);
  popSrc.stop(now + popDur + 0.02);

  // 2. Body thud — low-freq quick oscillator giving the deck weight.
  const thudOsc = ac.createOscillator();
  thudOsc.type = "sine";
  thudOsc.frequency.setValueAtTime(180, now);
  thudOsc.frequency.exponentialRampToValueAtTime(70, now + 0.09);
  const thudGain = ac.createGain();
  thudGain.gain.setValueAtTime(0.15, now);
  thudGain.gain.exponentialRampToValueAtTime(0.0001, now + 0.09);
  thudOsc.connect(thudGain);
  thudGain.connect(ac.destination);
  thudOsc.start(now);
  thudOsc.stop(now + 0.11);

  // 3. Flip whoosh — pitched-down sawtooth mimicking the board flipping through air.
  const flipOsc = ac.createOscillator();
  flipOsc.type = "sawtooth";
  flipOsc.frequency.setValueAtTime(520, now + 0.025);
  flipOsc.frequency.exponentialRampToValueAtTime(140, now + 0.2);
  const flipFilter = ac.createBiquadFilter();
  flipFilter.type = "lowpass";
  flipFilter.frequency.setValueAtTime(1600, now);
  flipFilter.Q.setValueAtTime(1.2, now);
  const flipGain = ac.createGain();
  flipGain.gain.setValueAtTime(0, now + 0.025);
  flipGain.gain.linearRampToValueAtTime(0.08, now + 0.06);
  flipGain.gain.exponentialRampToValueAtTime(0.0001, now + 0.22);
  flipOsc.connect(flipFilter);
  flipFilter.connect(flipGain);
  flipGain.connect(ac.destination);
  flipOsc.start(now + 0.025);
  flipOsc.stop(now + 0.24);

  // 4. Wheels roll — brief high-freq noise fade (griptape/wheels).
  const rollDur = 0.14;
  const rollBuf = ac.createBuffer(1, Math.floor(ac.sampleRate * rollDur), ac.sampleRate);
  const rollData = rollBuf.getChannelData(0);
  for (let i = 0; i < rollData.length; i += 1) {
    const t = i / rollData.length;
    rollData[i] = (Math.random() * 2 - 1) * (1 - t);
  }
  const rollSrc = ac.createBufferSource();
  rollSrc.buffer = rollBuf;
  const rollFilter = ac.createBiquadFilter();
  rollFilter.type = "highpass";
  rollFilter.frequency.setValueAtTime(2800, now);
  const rollGain = ac.createGain();
  rollGain.gain.setValueAtTime(0.06, now + 0.09);
  rollGain.gain.exponentialRampToValueAtTime(0.0001, now + 0.09 + rollDur);
  rollSrc.connect(rollFilter);
  rollFilter.connect(rollGain);
  rollGain.connect(ac.destination);
  rollSrc.start(now + 0.09);
  rollSrc.stop(now + 0.09 + rollDur);
};

export const playClickSound = () => {
  // All eras use the skate trick sound now.
  playSkateTrick();
};

export const playUpgradeSound = () => {
  const ac = getCtx();
  if (!ac) return;
  const now = ac.currentTime;
  [523.25, 659.25, 783.99].forEach((f, i) => {
    const osc = ac.createOscillator();
    const gain = ac.createGain();
    osc.type = "triangle";
    osc.frequency.setValueAtTime(f, now + i * 0.05);
    envelope(gain, 0.005, 0.12, 0.06, 0.35, now + i * 0.05);
    osc.connect(gain);
    gain.connect(ac.destination);
    osc.start(now + i * 0.05);
    osc.stop(now + i * 0.05 + 0.4);
  });
};

export const playEraSound = () => {
  const ac = getCtx();
  if (!ac) return;
  const now = ac.currentTime;
  const freqs = [261.63, 392.0, 523.25, 783.99];
  freqs.forEach((f, i) => {
    const osc = ac.createOscillator();
    const gain = ac.createGain();
    osc.type = "sine";
    osc.frequency.setValueAtTime(f, now + i * 0.08);
    envelope(gain, 0.01, 0.16, 0.09, 0.6, now + i * 0.08);
    osc.connect(gain);
    gain.connect(ac.destination);
    osc.start(now + i * 0.08);
    osc.stop(now + i * 0.08 + 0.7);
  });
};
